import { useState, useRef, useEffect } from "react";
import { Upload, X, Send, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/supabaseClient";
import { generateId } from "@/lib/id";

interface FeedbackFormProps {
  onSuccess?: () => void;
  initialScreenshot?: any | null;
}

export const FeedbackForm = ({ onSuccess, initialScreenshot }: FeedbackFormProps) => {
  const [type, setType] = useState<string>("bug_report");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [screenshot, setScreenshot] = useState<any | null>(null);
  const [screenshotPreview, setScreenshotPreview] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Pre-populate screenshot if provided
  useEffect(() => {
    if (initialScreenshot) {
      setScreenshot(initialScreenshot);
      const reader = new FileReader();
      reader.onloadend = () => {
        setScreenshotPreview(reader.result as string);
      };
      reader.readAsDataURL(initialScreenshot);
    }
  }, [initialScreenshot]);

  const handleScreenshotChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error("Screenshot must be less than 5MB");
        return;
      }
      if (!file.type.startsWith("image/")) {
        toast.error("Only image files are allowed");
        return;
      }
      setScreenshot(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setScreenshotPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeScreenshot = () => {
    setScreenshot(null);
    setScreenshotPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const uploadScreenshot = async (userId: string): Promise<string | null> => {
    if (!screenshot) return null;

    try {
      const fileExt = screenshot.name.split(".").pop();
      const fileName = `${userId}/${generateId()}.${fileExt}`;

      const { data, error } = await supabase.storage
        .from("feedback-screenshots")
        .upload(fileName, screenshot);

      if (error) throw error;

      // Get public URL
      const { data: urlData } = supabase.storage
        .from("feedback-screenshots")
        .getPublicUrl(fileName);

      return urlData.publicUrl;
    } catch (error) {
      console.error("Error uploading screenshot:", error);
      toast.error("Failed to upload screenshot");
      return null;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!title.trim()) {
      toast.error("Please enter a title");
      return;
    }

    if (!description.trim()) {
      toast.error("Please enter a description");
      return;
    }

    setIsSubmitting(true);

    try {
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("User not authenticated");

      // Upload screenshot if provided
      const screenshotUrl = await uploadScreenshot(user.id);

      // Collect metadata
      const userAgent = navigator.userAgent;
      const pageUrl = window.location.href;

      // Insert feedback
      const { data: feedback, error: insertError } = await supabase
        .from("feedback")
        .insert({
          user_id: user.id,
          type,
          title: title.trim(),
          description: description.trim(),
          screenshot_url: screenshotUrl,
          user_agent: userAgent,
          page_url: pageUrl,
        })
        .select()
        .single();

      if (insertError) throw insertError;

      // Send email notification
      try {
        await supabase.functions.invoke("send-feedback-notification", {
          body: {
            feedbackId: feedback.id,
            type,
            title: title.trim(),
            description: description.trim(),
            userEmail: user.email,
            userName: user.user_metadata?.display_name || user.email,
            screenshotUrl,
            userAgent,
            pageUrl,
          },
        });
      } catch (emailError) {
        console.error("Error sending notification email:", emailError);
        // Don't fail the whole operation if email fails
      }

      toast.success("Feedback submitted successfully!");
      
      // Reset form
      setTitle("");
      setDescription("");
      setType("bug_report");
      removeScreenshot();
      
      onSuccess?.();
    } catch (error: any) {
      console.error("Error submitting feedback:", error);
      toast.error(error.message || "Failed to submit feedback");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="space-y-2">
        <Label htmlFor="feedback-type">Feedback Type</Label>
        <Select value={type} onValueChange={setType}>
          <SelectTrigger id="feedback-type">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="bug_report">🐛 Bug Report</SelectItem>
            <SelectItem value="feature_request">✨ Feature Request</SelectItem>
            <SelectItem value="general_inquiry">💬 General Inquiry</SelectItem>
            <SelectItem value="other">📝 Other</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label htmlFor="feedback-title">Title</Label>
        <Input
          id="feedback-title"
          placeholder="Brief summary of your feedback"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          maxLength={200}
          required
        />
        <p className="text-xs text-muted-foreground">
          {title.length}/200 characters
        </p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="feedback-description">Description</Label>
        <Textarea
          id="feedback-description"
          placeholder="Please provide as much detail as possible..."
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={6}
          maxLength={2000}
          required
        />
        <p className="text-xs text-muted-foreground">
          {description.length}/2000 characters
        </p>
      </div>

      <div className="space-y-2">
        <Label>Screenshot (Optional)</Label>
        {!screenshotPreview ? (
          <div
            className="border-2 border-dashed border-muted-foreground/25 rounded-lg p-8 text-center cursor-pointer hover:border-primary/50 transition-colors"
            onClick={() => fileInputRef.current?.click()}
          >
            <Upload className="mx-auto h-12 w-12 text-muted-foreground/50 mb-4" />
            <p className="text-sm text-muted-foreground mb-1">
              Click to upload or drag and drop
            </p>
            <p className="text-xs text-muted-foreground">
              PNG, JPG, GIF up to 5MB
            </p>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleScreenshotChange}
              className="hidden"
            />
          </div>
        ) : (
          <Card>
            <CardContent className="p-4">
              <div className="relative">
                <img
                  src={screenshotPreview}
                  alt="Screenshot preview"
                  className="w-full rounded-md"
                />
                <Button
                  type="button"
                  variant="destructive"
                  size="icon"
                  className="absolute top-2 right-2"
                  onClick={removeScreenshot}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                {screenshot?.name}
              </p>
            </CardContent>
          </Card>
        )}
      </div>

      <Button type="submit" className="w-full" disabled={isSubmitting}>
        {isSubmitting ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Submitting...
          </>
        ) : (
          <>
            <Send className="mr-2 h-4 w-4" />
            Submit Feedback
          </>
        )}
      </Button>
    </form>
  );
};
